import { Col, Form, Input } from 'reactstrap';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, Controller } from 'react-hook-form';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  bookingStatusInitialValues,
  bookingStatusValidationSchema,
} from './constants';
import { fetchTravelRequestEnquiryFilter } from '../../../store/apis/travelService';
import CostField from './CostField';

const BookingStatus = forwardRef(
  (
    { data, selectedFlightOption, selectedHotelOption, onDataChange, status },
    ref,
  ) => {
    const validationContext = useMemo(
      () => ({
        isFlight: data?.Flight === 'Y',
        isHotel: data?.Hotel === 'Y',
        isVisa: data?.Visa === 'Y',
        isTI: data?.TravelInsurance === 'Y',
        isAT: data?.AirportTransfer === 'Y',

        // booking phases
        isCancel: status === 'Request for Cancellation',
        isRebook: status === 'Request for Rebooking',
        isQuote: status === 'Request for Quotation',
        isPendingBooking: status === 'Pending for Booking',
      }),
      [data, status],
    );

    const form = useForm({
      defaultValues: bookingStatusInitialValues,
      resolver: yupResolver(bookingStatusValidationSchema),
      context: validationContext,
      mode: 'onChange',
      shouldUnregister: true,
    });

    const {
      control,
      trigger,
      getValues,
      watch,
      setValue,
      reset,
      formState: { errors, isDirty },
    } = form;

    const didHydrateRef = useRef(false);

    // Fetch Travel Agency Options
    const { data: travelAgencyOptions } = useQuery({
      queryKey: ['travelTypeOptions'],
      queryFn: () =>
        fetchTravelRequestEnquiryFilter({
          codeType: 'TRAVEL_AGENCY',
        }),
    });

    useEffect(() => {
      const subscription = watch(values => {
        onDataChange(values);
      });
      return () => subscription.unsubscribe();
    }, [watch, onDataChange]);

    // Populate form values when data is available
    const mapApiToForm = d => ({
      // Flight
      flightStatus: d?.FlightStatus ?? '',
      flightComments: d?.FlightComments ?? '',
      flightCost:
        d?.FlightCost !== '0' ? Number(d?.FlightCost).toFixed(2) : null,

      // Hotel
      hotelStatus: d?.HotelStatus ?? '',
      hotelComments: d?.HotelComments ?? '',
      hotelCost: d?.HotelCost !== '0' ? Number(d?.HotelCost).toFixed(2) : null,

      // Airport Transfer
      airportTransferComments: d?.AirportTransferComments ?? '',
      airportTransferStatus: d?.AirportTransferStatus ?? '',
      airportTransferQuoteCost: Number(
        d?.AirportTransferQuoteCost ?? null,
      ).toFixed(2),
      airportTransferCost:
        d?.AirportTransferCost !== '0'
          ? Number(d?.AirportTransferCost).toFixed(2)
          : null,
      airportTransferTravelAgency: d?.Travel_Agency_Air_Transfer?.trim() ?? '',

      // Visa
      visaComments: d?.VisaComments ?? '',
      visaStatus: d?.VisaStatus ?? '',
      visaQuoteCost: Number(d?.VisaQuoteCost).toFixed(2) ?? null,
      visaCost: d?.VisaCost !== '0' ? Number(d?.VisaCost).toFixed(2) : null,
      visaTravelAgency: d?.Travel_Agency_Visa?.trim() ?? '',

      // Travel Insurance
      travelInsuranceComments: d?.InsuranceComments ?? '',
      travelInsuranceStatus: d?.InsuranceStatus ?? '',
      travelInsuranceQuoteCost: Number(d?.InsuranceQuoteCost ?? null).toFixed(
        2,
      ),
      travelInsuranceCost:
        d?.InsuranceCost !== '0' ? Number(d?.InsuranceCost).toFixed(2) : null,
      travelInsuranceTravelAgency: d?.Travel_Agency_Insurance?.trim() ?? '',
    });

    useEffect(() => {
      if (!data) return;
      // hydrate once OR only when form not dirty
      if (!didHydrateRef.current || !isDirty) {
        reset(mapApiToForm(data), { keepDirtyValues: true }); // RHF supports this
        didHydrateRef.current = true;
      }
    }, [data, reset, isDirty]);

    useEffect(() => {
      if (selectedFlightOption?.quoteValue != null) {
        const q = Number(selectedFlightOption?.quoteValue).toFixed(2);
        setValue('flightQuoteCost', q, { shouldDirty: true });
      }
    }, [selectedFlightOption, setValue]);

    useEffect(() => {
      if (selectedHotelOption?.quoteValue != null) {
        const q = Number(selectedHotelOption?.quoteValue).toFixed(2);
        setValue('hotelQuoteCost', q, { shouldDirty: true });
      }
    }, [selectedHotelOption, setValue]);

    useImperativeHandle(ref, () => ({
      triggerAll: () => trigger(), // validate all fields in this subform
      triggerFields: names => trigger(names), // validate specific fields if you prefer
      getValues: () => getValues(),
      hasErrors: () => Object.keys(errors || {}).length > 0,
    }));

    return (
      <div className="d-flex flex-column gap-3 ba-form">
        <div>
          <span className="fw-semibold">Booking Status</span>
        </div>
        <div>
          <Form className="d-flex flex-column gap-2">
            {data?.Flight === 'Y' && (
              <div className="d-flex flex-row align-items-center justify-content-between gap-2">
                <Col className="external-filter-col">
                  <span className="ba-form-label">
                    Flight Quote Cost<span className="text-danger">*</span>
                  </span>
                  <Controller
                    control={control}
                    name="flightQuoteCost"
                    render={({ field, fieldState }) => (
                      <Input
                        {...field}
                        disabled
                        type="number"
                        placeholder="0"
                        className={`external-filter-input ${fieldState.error ? 'is-invalid' : ''}`}
                        onChange={e => {
                          const v = e.target.value;
                          field.onChange(v === '' ? undefined : Number(v));
                        }}
                        onBlur={e => {
                          const val = e.target.value;
                          field.onChange(
                            val === '' ? '' : parseFloat(val).toFixed(2),
                          );
                        }}
                      />
                    )}
                  />
                  {errors.flightQuoteCost && (
                    <span className="text-danger">
                      {errors.flightQuoteCost.message}
                    </span>
                  )}
                </Col>

                <CostField service="Flight" status={status} control={control} />

                <Col className="external-filter-col">
                  <span className="ba-form-label">
                    Flight Status<span className="text-danger">*</span>
                  </span>
                  <Controller
                    control={control}
                    name="flightStatus"
                    render={({ field, fieldState }) => (
                      <Input
                        type="select"
                        {...field}
                        className={`external-filter-input ${fieldState.error ? 'is-invalid' : ''}`}
                      >
                        <option value="">Select</option>
                        <option value="Confirmed">Confirmed</option>
                        <option value="Pending">Pending</option>
                      </Input>
                    )}
                  />
                  {errors.flightStatus && (
                    <span className="text-danger">
                      {errors.flightStatus.message}
                    </span>
                  )}
                </Col>
                <Col className="external-filter-col">
                  <span className="ba-form-label">
                    Booking Reference<span className="text-danger">*</span>
                  </span>
                  <Controller
                    control={control}
                    name="flightComments"
                    render={({ field }) => (
                      <Input
                        {...field}
                        placeholder="Flight Comments"
                        className="external-filter-input"
                      />
                    )}
                  />
                </Col>
              </div>
            )}

            {data?.Hotel === 'Y' && (
              <div className="d-flex flex-row align-items-center justify-content-between gap-2">
                {/* Hotel Quote */}
                <Col className="external-filter-col">
                  <span className="ba-form-label">
                    Hotel Quote Cost<span className="text-danger">*</span>
                  </span>
                  <Controller
                    control={control}
                    name="hotelQuoteCost"
                    render={({ field, fieldState }) => (
                      <Input
                        {...field}
                        disabled
                        type="number"
                        placeholder="0"
                        className={`external-filter-input ${fieldState.error ? 'is-invalid' : ''}`}
                        onChange={e => {
                          const v = e.target.value;
                          field.onChange(v === '' ? undefined : Number(v));
                        }}
                        onBlur={e => {
                          const val = e.target.value;
                          field.onChange(
                            val === '' ? '' : parseFloat(val).toFixed(2),
                          );
                        }}
                      />
                    )}
                  />
                  {errors.hotelQuoteCost && (
                    <span className="text-danger">
                      {errors.hotelQuoteCost.message}
                    </span>
                  )}
                </Col>

                <CostField service="Hotel" status={status} control={control} />

                <Col className="external-filter-col">
                  <span className="ba-form-label">
                    Hotel Status<span className="text-danger">*</span>
                  </span>
                  <Controller
                    control={control}
                    name="hotelStatus"
                    render={({ field, fieldState }) => (
                      <Input
                        type="select"
                        {...field}
                        className={`external-filter-input ${fieldState.error ? 'is-invalid' : ''}`}
                      >
                        <option value="">Select</option>
                        <option value="Confirmed">Confirmed</option>
                        <option value="Pending">Pending</option>
                      </Input>
                    )}
                  />
                  {errors.hotelStatus && (
                    <span className="text-danger">
                      {errors.hotelStatus.message}
                    </span>
                  )}
                </Col>
                <Col className="external-filter-col">
                  <span className="ba-form-label">
                    Booking Reference<span className="text-danger">*</span>
                  </span>
                  <Controller
                    control={control}
                    name="hotelComments"
                    render={({ field, fieldState }) => (
                      <Input
                        {...field}
                        placeholder="Hotel Comments"
                        className={`external-filter-input ${fieldState.error ? 'is-invalid' : ''}`}
                      />
                    )}
                  />
                  {errors.hotelComments && (
                    <span className="text-danger">
                      {errors.hotelComments.message}
                    </span>
                  )}
                </Col>
              </div>
            )}
            {data?.Visa === 'Y' && (
              <div className="d-flex flex-row align-items-center justify-content-between gap-2">
                <Col className="external-filter-col">
                  <span className="ba-form-label">
                    Visa Quote Cost
                    <span className="text-danger">*</span>
                  </span>
                  <Controller
                    control={control}
                    name="visaQuoteCost"
                    render={({ field, fieldState }) => (
                      <Input
                        {...field}
                        disabled
                        type="number"
                        placeholder="0"
                        className={`external-filter-input ${fieldState.error ? 'is-invalid' : ''}`}
                        onChange={e => {
                          const v = e.target.value;
                          field.onChange(v === '' ? undefined : Number(v));
                        }}
                        onBlur={e => {
                          const val = e.target.value;
                          field.onChange(
                            val === '' ? '' : parseFloat(val).toFixed(2),
                          );
                        }}
                      />
                    )}
                  />
                  {errors.visaQuoteCost && (
                    <span className="text-danger">
                      {errors.visaQuoteCost.message}
                    </span>
                  )}
                </Col>

                <CostField service="Visa" status={status} control={control} />

                <Col className="external-filter-col">
                  <span className="ba-form-label">
                    Visa Status<span className="text-danger">*</span>
                  </span>
                  <Controller
                    control={control}
                    name="visaStatus"
                    render={({ field, fieldState }) => (
                      <Input
                        type="select"
                        {...field}
                        className={`external-filter-input ${fieldState.error ? 'is-invalid' : ''}`}
                      >
                        <option value="">Select</option>
                        <option value="Confirmed">Confirmed</option>
                        <option value="Pending">Pending</option>
                      </Input>
                    )}
                  />
                  {errors.visaStatus && (
                    <span className="text-danger">
                      {errors.visaStatus.message}
                    </span>
                  )}
                </Col>
                <Col className="external-filter-col">
                  <span className="ba-form-label">Travel Agency</span>
                  <Controller
                    control={control}
                    name="visaTravelAgency"
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="select"
                        className="external-filter-input"
                      >
                        <option value="">Select Travel Agency</option>
                        {travelAgencyOptions?.map(option => (
                          <option key={option.Code} value={option.Code}>
                            {option.Description}
                          </option>
                        ))}
                      </Input>
                    )}
                  />
                </Col>
                <Col className="external-filter-col">
                  <span className="ba-form-label">
                    Booking Reference<span className="text-danger">*</span>
                  </span>
                  <Controller
                    control={control}
                    name="visaComments"
                    render={({ field, fieldState }) => (
                      <Input
                        {...field}
                        placeholder="Visa Comments"
                        className={`external-filter-input ${fieldState.error ? 'is-invalid' : ''}`}
                      />
                    )}
                  />
                  {errors.visaComments && (
                    <span className="text-danger">
                      {errors.visaComments.message}
                    </span>
                  )}
                </Col>
              </div>
            )}
            {data?.TravelInsurance === 'Y' && (
              <div className="d-flex flex-row align-items-center justify-content-between gap-2">
                <Col className="external-filter-col">
                  <span className="ba-form-label">
                    Travel Insurance Quote Cost
                    <span className="text-danger">*</span>
                  </span>
                  <Controller
                    control={control}
                    name="travelInsuranceQuoteCost"
                    render={({ field, fieldState }) => (
                      <Input
                        {...field}
                        disabled
                        placeholder="0"
                        type="number"
                        className={`external-filter-input ${fieldState.error ? 'is-invalid' : ''}`}
                        onChange={e => {
                          const v = e.target.value;
                          field.onChange(v === '' ? undefined : Number(v));
                        }}
                        onBlur={e => {
                          const val = e.target.value;
                          field.onChange(
                            val === '' ? '' : parseFloat(val).toFixed(2),
                          );
                        }}
                      />
                    )}
                  />
                  {errors.travelInsuranceQuoteCost && (
                    <span className="text-danger">
                      {errors.travelInsuranceQuoteCost.message}
                    </span>
                  )}
                </Col>
                <CostField
                  service="TravelInsurance"
                  status={status}
                  control={control}
                />
                <Col className="external-filter-col">
                  <span className="ba-form-label">
                    Travel Insurance Status
                    <span className="text-danger">*</span>
                  </span>
                  <Controller
                    control={control}
                    name="travelInsuranceStatus"
                    render={({ field, fieldState }) => (
                      <Input
                        type="select"
                        {...field}
                        className={`external-filter-input ${fieldState.error ? 'is-invalid' : ''}`}
                      >
                        <option value="">Select</option>
                        <option value="Confirmed">Confirmed</option>
                        <option value="Pending">Pending</option>
                      </Input>
                    )}
                  />
                  {errors.travelInsuranceStatus && (
                    <span className="text-danger">
                      {errors.travelInsuranceStatus.message}
                    </span>
                  )}
                </Col>
                <Col className="external-filter-col">
                  <span className="ba-form-label">Travel Agency</span>
                  <Controller
                    control={control}
                    name="travelInsuranceTravelAgency"
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="select"
                        className="external-filter-input"
                      >
                        <option value="">Select Travel Agency</option>
                        {travelAgencyOptions?.map(option => (
                          <option key={option.Code} value={option.Code}>
                            {option.Description}
                          </option>
                        ))}
                      </Input>
                    )}
                  />
                </Col>
                <Col className="external-filter-col">
                  <span className="ba-form-label">
                    Booking Reference<span className="text-danger">*</span>
                  </span>
                  <Controller
                    control={control}
                    name="travelInsuranceComments"
                    render={({ field, fieldState }) => (
                      <Input
                        {...field}
                        placeholder="Travel Insurance Comments"
                        className={`external-filter-input ${fieldState.error ? 'is-invalid' : ''}`}
                      />
                    )}
                  />
                  {errors.travelInsuranceComments && (
                    <span className="text-danger">
                      {errors.travelInsuranceComments.message}
                    </span>
                  )}
                </Col>
              </div>
            )}
            {data?.AirportTransfer === 'Y' && (
              <div className="d-flex flex-row align-items-center justify-content-between gap-2">
                <Col className="external-filter-col">
                  <span className="ba-form-label">
                    Airport Transfer Quote Cost
                    <span className="text-danger">*</span>
                  </span>
                  <Controller
                    control={control}
                    name="airportTransferQuoteCost"
                    render={({ field, fieldState }) => (
                      <Input
                        {...field}
                        disabled
                        type="number"
                        placeholder="0"
                        className={`external-filter-input ${fieldState.error ? 'is-invalid' : ''}`}
                        onChange={e => {
                          const v = e.target.value;
                          field.onChange(v === '' ? undefined : Number(v));
                        }}
                        onBlur={e => {
                          const val = e.target.value;
                          field.onChange(
                            val === '' ? '' : parseFloat(val).toFixed(2),
                          );
                        }}
                      />
                    )}
                  />
                  {errors.airportTransferQuoteCost && (
                    <span className="text-danger">
                      {errors.airportTransferQuoteCost.message}
                    </span>
                  )}
                </Col>
                <CostField
                  service="AirportTransfer"
                  status={status}
                  control={control}
                />
                <Col className="external-filter-col">
                  <span className="ba-form-label">
                    Airport Transfer Status
                    <span className="text-danger">*</span>
                  </span>
                  <Controller
                    control={control}
                    name="airportTransferStatus"
                    render={({ field, fieldState }) => (
                      <Input
                        type="select"
                        {...field}
                        className={`external-filter-input ${fieldState.error ? 'is-invalid' : ''}`}
                      >
                        <option value="">Select</option>
                        <option value="Confirmed">Confirmed</option>
                        <option value="Pending">Pending</option>
                      </Input>
                    )}
                  />
                  {errors.airportTransferStatus && (
                    <span className="text-danger">
                      {errors.airportTransferStatus.message}
                    </span>
                  )}
                </Col>
                <Col className="external-filter-col">
                  <span className="ba-form-label">Travel Agency</span>
                  <Controller
                    control={control}
                    name="airportTransferTravelAgency"
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="select"
                        className="external-filter-input"
                      >
                        <option value="">Select Travel Agency</option>
                        {travelAgencyOptions?.map(option => (
                          <option key={option.Code} value={option.Code}>
                            {option.Description}
                          </option>
                        ))}
                      </Input>
                    )}
                  />
                </Col>
                <Col className="external-filter-col">
                  <span className="ba-form-label">
                    Booking Reference<span className="text-danger">*</span>
                  </span>
                  <Controller
                    control={control}
                    name="airportTransferComments"
                    render={({ field, fieldState }) => (
                      <Input
                        {...field}
                        placeholder="Airport Transfer Comments"
                        className={`external-filter-input ${fieldState.error ? 'is-invalid' : ''}`}
                      />
                    )}
                  />
                  {errors.airportTransferComments && (
                    <span className="text-danger">
                      {errors.airportTransferComments.message}
                    </span>
                  )}
                </Col>
              </div>
            )}
          </Form>
        </div>
      </div>
    );
  },
);

export default BookingStatus;
